import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, api, can } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { ago, datetime, huf, ms, outcomeLabel, outcomeTone, percent, usd } from "../format";
import { useApi } from "../useApi";

interface JobSummary {
  id: string;
  created_at: string;
  status: string;
  outcome: string | null;
  document_type: string | null;
  legibility: string | null;
  confidence: number | null;
  page_count: number;
  total_huf: number | null;
  duration_ms: number | null;
  item_count: number;
  unresolved_count: number;
  rule_version_id: number | null;
  error: string | null;
}

interface ItemTrace {
  raw_text: string;
  marking: string;
  resolved_code: string | null;
  match_method: string;
  match_score: number;
  confidence: number;
  applied_rules: string[];
  unit_price: number | null;
  billed: boolean;
  /** A tétel neve a mostani árlistán, és hogy megválaszolatlan kérdés megy-e róla. */
  resolved_name?: string;
  pending_question?: boolean;
}

interface CallTrace {
  task: string;
  provider: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  latency_ms: number;
  success: boolean;
  attempts: number;
  used_fallback: boolean;
  repaired_schema: boolean;
  error_type: string | null;
}

interface JobDetail extends JobSummary {
  notes: Record<string, unknown>;
  raw_deleted_at: string | null;
  extract_prompt_version: string | null;
  items: ItemTrace[];
  llm_calls: CallTrace[];
}

const METHOD_LABEL: Record<string, string> = {
  pontos_nev: "pontos név",
  irasmod: "más írásmód",
  alias: "alias",
  fuzzy: "hasonlóság",
  reszleges: "részleges egyezés",
  leiras: "leírás alapján",
  szemantikus: "jelentés",
  azonos_ar: "azonos árú változat",
  egyedi_allergen: "egyedi allergén (lista)",
  ai_parositas: "modell párosította",
  megerositve: "megerősítve",
  nem_tudja: "a látogató nem tudta",
  nincs_talalat: "nincs találat",
};

export function ReferralsView({
  session,
  onlyReview = false,
}: {
  session: Session | null;
  onlyReview?: boolean;
}): JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);
  const query = `/admin/v1/referrals?days=90&limit=50${onlyReview ? "&needs_review=true" : ""}`;
  const { data, error, loading } = useApi<{ total: number; items: JobSummary[] }>(query, [
    onlyReview,
  ]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;

  return (
    <>
      <Card
        title={onlyReview ? "Figyelmet igénylő feldolgozások" : "Feldolgozások"}
        hint={
          onlyReview
            ? "Amit a rendszer nem vállalt egyedül — ez nem hibalista, hanem munkasor."
            : `Az elmúlt 90 nap · ${data?.total ?? 0} db`
        }
      >
        {!data?.items.length ? (
          <Empty>
            {onlyReview
              ? "Nincs figyelmet igénylő feldolgozás. "
              : "Még nem érkezett beutaló. "}
            A beutalók a chatbe feltöltött fényképekből keletkeznek.
          </Empty>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th>Mikor</th>
                <th>Kimenetel</th>
                <th>Típus</th>
                <th className="num">Tétel</th>
                <th className="num">Összeg</th>
                <th className="num">Biztonság</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.flatMap((job) => [
                <tr
                  key={job.id}
                  className={selected === job.id ? "is-selected" : ""}
                  onClick={() => setSelected(selected === job.id ? null : job.id)}
                >
                  <td title={datetime(job.created_at)}>{ago(job.created_at)}</td>
                  <td>
                    {job.status === "hiba" ? (
                      <Pill tone="bad">Hiba</Pill>
                    ) : (
                      <Pill tone={outcomeTone(job.outcome)}>{outcomeLabel(job.outcome)}</Pill>
                    )}
                  </td>
                  <td className="muted">{job.document_type ?? "—"}</td>
                  <td className="num">
                    {job.item_count}
                    {job.unresolved_count > 0 && (
                      <span className="muted"> ({job.unresolved_count} nyitott)</span>
                    )}
                  </td>
                  <td className="num">{huf(job.total_huf)}</td>
                  <td className="num">{percent(job.confidence)}</td>
                  <td className="num muted">{selected === job.id ? "▾" : "▸"}</td>
                </tr>,
                // A részletek OTT nyílnak, ahol a munkatárs kattintott — nem a
                // lista alján, ahová le kellene görgetnie.
                selected === job.id && (
                  <tr key={`${job.id}-reszletek`} className="rows__detail">
                    <td colSpan={7}>
                      <Detail jobId={job.id} session={session} />
                    </td>
                  </tr>
                ),
              ])}
            </tbody>
          </table>
        )}
      </Card>

    </>
  );
}

function Detail({
  jobId,
  session,
}: {
  jobId: string;
  session: Session | null;
}): JSX.Element {
  const { data, error, loading, reload } = useApi<JobDetail>(`/admin/v1/referrals/${jobId}`);
  const [replay, setReplay] = useState<string | null>(null);
  const [replayed, setReplayed] = useState<{ version: number | null; items: ItemTrace[] } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const doReplay = async (): Promise<void> => {
    setBusy(true);
    setReplay(null);
    try {
      const result = await api.post<{
        changed: boolean;
        before: { total_huf: number | null; feloldatlan: string[] };
        after: { total_huf: number | null; feloldatlan: string[] };
        rule_version_id?: number | null;
        items?: ItemTrace[];
      }>(`/admin/v1/review/replay/${jobId}`);
      // A tételek táblázata innentől a MOSTANI szabályok szerinti állapotot
      // mutatja — a javítás hatása ott látszik, ahol a hiba látszott.
      if (result.items) {
        setReplayed({ version: result.rule_version_id ?? null, items: result.items });
      }
      setReplay(
        result.changed
          ? `Változott: ${huf(result.before.total_huf)} → ${huf(result.after.total_huf)}` +
              (result.after.feloldatlan.length
                ? ` · még nyitott: ${result.after.feloldatlan.join(", ")}`
                : " · minden tétel azonosítva")
          : "Az eredmény változatlan a mostani szabályokkal.",
      );
      reload();
    } catch (e: unknown) {
      setReplay(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <></>;

  return (
    <Card
      title="Nyomvonal"
      hint="Minden sorról megmondható, honnan származik és hogyan lett belőle ár."
      actions={
        can(session, "ujrajatszik") && (
          <Button onClick={doReplay} disabled={busy}>
            {busy ? "Fut…" : "Újrajátszás"}
          </Button>
        )
      }
    >
      {replay && <p className="notice notice--info">{replay}</p>}

      <dl className="facts">
        <div>
          <dt>Azonosító</dt>
          <dd className="mono">{data.id}</dd>
        </div>
        <div>
          <dt>Időpont</dt>
          <dd>{datetime(data.created_at)}</dd>
        </div>
        <div>
          <dt>Olvashatóság</dt>
          <dd>{data.legibility ?? "—"}</dd>
        </div>
        <div>
          <dt>Szabályverzió</dt>
          <dd>{data.rule_version_id ? `#${data.rule_version_id}` : "—"}</dd>
        </div>
        <div>
          <dt>Feldolgozási idő</dt>
          <dd>{ms(data.duration_ms)}</dd>
        </div>
        <div>
          <dt>Nyers fájl törölve</dt>
          <dd>{data.raw_deleted_at ? datetime(data.raw_deleted_at) : "—"}</dd>
        </div>
      </dl>

      {data.error && <ErrorNote>{data.error}</ErrorNote>}

      <h3>Felismert tételek</h3>
      {replayed && (
        <p className="notice notice--info">
          A táblázat a mostani árlistával{replayed.version ? ` (#${replayed.version})` : ""}{" "}
          újraszámolt állapotot mutatja. A látogató által akkor kapott eredmény
          változatlanul megmarad.
        </p>
      )}
      {data.items.length === 0 ? (
        <Empty>Ehhez a feldolgozáshoz nincs rögzített tétel.</Empty>
      ) : (
        <table className="rows">
          <thead>
            <tr>
              <th>A beutalón</th>
              <th>Azonosítva</th>
              <th>Módszer</th>
              <th className="num">Pont</th>
              <th className="num">Ár</th>
              <th>Szabályok</th>
            </tr>
          </thead>
          <tbody>
            {(replayed?.items ?? data.items).map((item, index) => (
              <tr key={`${item.raw_text}-${index}`} className={item.billed ? "" : "is-dim"}>
                <td>
                  {item.raw_text}
                  {item.marking === "latogato_kerte" && (
                    <div className="muted">a látogató külön kérése (az üzenetéből)</div>
                  )}
                </td>
                <td>
                  {item.resolved_code ? (
                    item.resolved_name ? (
                      <span title={item.resolved_code}>{item.resolved_name}</span>
                    ) : (
                      <span className="mono">{item.resolved_code}</span>
                    )
                  ) : item.pending_question ? (
                    // Az árlistán szerepel, de több vizsgálatra is illik: a
                    // látogató visszakérdezést kap róla. Nem hiányzó alias.
                    <Pill tone="info">visszakérdezés megy róla</Pill>
                  ) : item.match_method === "nem_tudja" ? (
                    // A látogató kérdést kapott, és azt felelte: nem tudja.
                    // Ez nem hiányzó név — a rendelőnek kell egyeztetnie vele.
                    <Pill tone="warn">egyeztetni kell</Pill>
                  ) : (
                    <Pill tone="warn">nincs találat</Pill>
                  )}
                </td>
                <td className="muted">
                  {item.pending_question
                    ? "több tételre illik"
                    : (METHOD_LABEL[item.match_method] ?? item.match_method)}
                </td>
                <td className="num">{item.match_score ? item.match_score.toFixed(0) : "—"}</td>
                <td className="num">{huf(item.unit_price)}</td>
                <td className="muted">{item.applied_rules.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Modellhívások</h3>
      {data.llm_calls.length === 0 ? (
        <Empty>Ehhez a feldolgozáshoz nincs rögzített modellhívás.</Empty>
      ) : (
        <table className="rows">
          <thead>
            <tr>
              <th>Feladat</th>
              <th>Modell</th>
              <th className="num">Token</th>
              <th className="num">Költség</th>
              <th className="num">Idő</th>
              <th>Megjegyzés</th>
            </tr>
          </thead>
          <tbody>
            {data.llm_calls.map((call, index) => (
              <tr key={`${call.task}-${index}`}>
                <td>{call.task}</td>
                <td>
                  <strong>{call.model}</strong>
                  <span className="muted"> · {call.provider}</span>
                </td>
                <td className="num">{call.prompt_tokens + call.completion_tokens}</td>
                <td className="num">{usd(call.cost_usd)}</td>
                <td className="num">{ms(call.latency_ms)}</td>
                <td className="muted">
                  {!call.success && <Pill tone="bad">hiba</Pill>}
                  {call.used_fallback && <Pill tone="warn">tartalék modell</Pill>}
                  {call.repaired_schema && <Pill tone="warn">séma javítva</Pill>}
                  {call.attempts > 1 && <span> {call.attempts} próba</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
